import { useEffect, useRef, useState } from 'react';

const differentiators = [
  {
    num: '01',
    icon: 'ri-hand-coin-line',
    title: 'Performance-Based Fees.',
    subtitle: 'We put our money where our promise is.',
    body: 'Our management fee is split: a small base, and an incentive tied directly to your RevPAR, NOI, and cost targets. If we don\'t hit the numbers, our fee doesn\'t either. No other Florida management firm offers this. It\'s not a tagline — it\'s in the contract.',
    badge: 'Owners see an average 18–24% NOI improvement within the first 12 months of management — because our incentives are perfectly aligned with yours.',
    badgeIcon: 'ri-line-chart-line',
  },
  {
    num: '02',
    icon: 'ri-user-star-line',
    title: 'One Partner. Not a Pipeline.',
    subtitle: 'Every client has a named senior partner.',
    body: 'Every 360 client has a named senior partner with direct accountability for your property. You call them, not a support queue. Weekly performance reporting, monthly strategy reviews, and a team that knows your hotel by name — not by account number.',
    badge: 'Weekly owner reports. Monthly strategy calls. A direct line to your senior partner — not a ticketing system or a call center.',
    badgeIcon: 'ri-shield-check-line',
  },
  {
    num: '03',
    icon: 'ri-map-pin-2-line',
    title: 'Born in Florida. Built for Florida.',
    subtitle: 'We don\'t parachute in — we live here.',
    body: 'We\'re a second-generation Florida hospitality group with over 100 years of combined experience across North, Central, Southwest, and Southeast Florida. Every market insight, every vendor relationship, every market dynamic is local.',
    badge: 'Deep roots across North, Central, Southwest, and Southeast Florida — giving your property a genuine local edge in every market we operate.',
    badgeIcon: 'ri-home-heart-line',
  },
];

export default function WhySection() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`mb-16 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">Why 360</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-light text-gray-900 leading-tight max-w-3xl mx-auto">
            We&apos;re Different in the Three Ways
            <br />
            <span className="italic text-gray-500">That Actually Matter</span>
          </h2>
        </div>

        {/* Differentiators */}
        <div className="space-y-8">
          {differentiators.map((item, index) => (
            <div
              key={index}
              className={`group grid lg:grid-cols-12 gap-8 items-start border border-gray-100 rounded-2xl p-8 hover:border-amber-200 hover:bg-amber-50/30 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Left — number + icon */}
              <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-4">
                <span className="text-3xl font-extralight text-amber-400/60">{item.num}</span>
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl group-hover:bg-amber-100 transition-colors">
                  <i className={`${item.icon} text-2xl text-amber-600`}></i>
                </div>
              </div>

              {/* Center — main content */}
              <div className="lg:col-span-7">
                <h3 className="text-2xl font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-base text-amber-600 font-light mb-4">{item.subtitle}</p>
                <p className="text-base text-gray-600 font-light leading-relaxed">{item.body}</p>
              </div>

              {/* Right — differentiator badge */}
              <div className="lg:col-span-4">
                <div className="bg-slate-900 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.badgeIcon} text-amber-400 text-base`}></i>
                    </div>
                    <p className="text-sm text-gray-300 font-light leading-relaxed">{item.badge}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
